import * as React from "react"
import { DayPicker, type DayPickerProps } from "react-day-picker"

import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "../../cn"

/* ── Variants ─────────────────────────────────────────────── */

const dayBaseClasses = [
  "relative p-0 text-center text-sm",
  "focus-within:relative focus-within:z-20",
]

const dayButtonClasses = [
  "inline-flex size-8 cursor-pointer items-center justify-center rounded-md",
  "text-sm font-normal text-coval-text tabular-nums",
  "transition-colors duration-150",
  "hover:bg-surface-1 hover:text-coval-text",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-text",
  "disabled:pointer-events-none disabled:opacity-40",
]

/**
 * Selection is painted on the day cell rather than the button so a range reads
 * as one continuous band: the cell carries the fill, the button the rounding.
 */
const calendarClassNames = {
  root: "w-fit",
  months: "relative flex flex-col gap-4 sm:flex-row",
  month: "flex flex-col gap-4",
  month_caption: "flex h-8 items-center justify-center",
  caption_label: "text-sm font-medium text-coval-text",
  nav: "absolute inset-x-0 top-0 flex h-8 items-center justify-between px-1",
  button_previous: cn(
    "inline-flex size-7 cursor-pointer items-center justify-center rounded-md",
    "text-coval-text-muted transition-colors duration-150",
    "hover:bg-surface-1 hover:text-coval-text",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-text",
    "disabled:pointer-events-none disabled:opacity-40",
  ),
  button_next: cn(
    "inline-flex size-7 cursor-pointer items-center justify-center rounded-md",
    "text-coval-text-muted transition-colors duration-150",
    "hover:bg-surface-1 hover:text-coval-text",
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-text",
    "disabled:pointer-events-none disabled:opacity-40",
  ),
  chevron: "size-4",
  month_grid: "w-full border-collapse",
  weekdays: "flex",
  weekday: "w-8 text-tiny font-normal text-coval-text-muted",
  week: "mt-1 flex w-full",
  day: cn(dayBaseClasses),
  day_button: cn(dayButtonClasses),
  today: "font-semibold text-coval-text underline underline-offset-4",
  outside: "text-coval-text-muted opacity-50",
  disabled: "opacity-40",
  hidden: "invisible",
  selected: "",
  range_start: "rounded-l-md bg-surface-selected",
  range_middle: "bg-surface-selected",
  range_end: "rounded-r-md bg-surface-selected",
  dropdowns: "flex items-center gap-2",
  months_dropdown: "rounded-md bg-surface-0 px-2 py-1 text-sm text-coval-text",
  years_dropdown: "rounded-md bg-surface-0 px-2 py-1 text-sm text-coval-text",
} satisfies DayPickerProps["classNames"]

/* ── Component ────────────────────────────────────────────── */

export type CalendarProps = DayPickerProps

/**
 * Month grid for picking a day or a range, styled on the design tokens.
 *
 * Wraps `react-day-picker`, so every selection mode and modifier it supports
 * passes straight through; only the presentation is ours.
 */
function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{ ...calendarClassNames, ...classNames }}
      components={{
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  )
}

Calendar.displayName = "Calendar"

function CalendarChevron({
  className,
  orientation,
}: {
  className?: string
  orientation?: "left" | "right" | "up" | "down"
}) {
  const Icon = orientation === "left" ? ChevronLeft : ChevronRight
  return <Icon className={className} aria-hidden />
}

/**
 * The selected day reads as filled, and the ends of a range keep that fill
 * while the days between them take the softer band from the cell.
 */
function CalendarDayButton({
  className,
  modifiers,
  day: _day,
  ...props
}: React.ComponentProps<
  NonNullable<NonNullable<DayPickerProps["components"]>["DayButton"]>
>) {
  const isRangeMiddle = modifiers.range_middle
  const isFilled = modifiers.selected && !isRangeMiddle

  return (
    <button
      type="button"
      className={cn(
        className,
        isFilled &&
          "bg-coval-contrast font-medium text-coval-text-on-contrast hover:bg-coval-contrast-hover hover:text-coval-text-on-contrast",
        isRangeMiddle && "rounded-none",
      )}
      {...props}
    />
  )
}

export { Calendar, calendarClassNames }
